import { router, usePage } from "@inertiajs/vue3";
import mitt from "mitt";
import { computed, ref, watch } from "vue";

import { getCookie } from "../utils";
import { setLocale } from "./i18n";
import { initializeAmplitudeEngagement, trackPageView } from "./utils/amplitude";
import { onCookieConsentChangedElsewhere } from "./utils/cookieConsent";
import {
    applyIntercomServerResponse,
    markIntercomVisitStarted,
    recordIntercomServerResponse,
    refreshIntercomMessengerForConsentChange,
    shutdownIntercomMessengerWithOtherTabs,
    syncIntercomMessengerFromHistory,
} from "./utils/intercom";

// Exported for direct testing - see BRA-1045 AC18. Inertia visits don't re-render the base
// template, so when the resolved locale changes (a full navigation, or a partial reload whose
// `only` list includes `locale`), the server-rendered `<html lang>` goes stale until synced here.
export function syncDocumentLang(locale) {
    if (locale && globalThis.document.documentElement.lang !== locale) {
        globalThis.document.documentElement.lang = locale;
    }
}

// Exported for direct testing (BRA-1044). The same "locale" shared prop that drives <html lang>
// also drives vue-i18n - a single source of truth rather than a second, client-derived one.
// setLocale loads that locale's catalogue chunk before switching vue-i18n's active locale, so a
// language change never flashes a raw key or a half-translated page.
export function handleLocaleChange(locale) {
    syncDocumentLang(locale);
    if (locale) {
        void setLocale(locale);
    }
}

export default {
    /*
     * `install` only runs once, and is passed the props of the page that is
     * first rendered at page load. These props are used as the initial value
     * for some values that are immutable (events url, etc).
     */
    install: (app, initialProps) => {
        const { event_url, feedback_mailto, start_chat_url, loading_image_src, user_details, is_debug, public_runtime_config } = initialProps;

        const eventContext = ref({});

        const sendEvent = async function (eventType, data) {
            const response = await fetch(event_url, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "X-Requested-With": "XMLHttpRequest",
                    "X-CSRFToken": getCookie("csrftoken"),
                },
                body: JSON.stringify({
                    event_type: eventType,
                    ...data,
                }),
            });

            const body = await response.json();
            body.status = response.status;

            if (!response.ok) {
                return Promise.reject(body);
            }

            return body;
        };

        const sendSocketEvent = function (eventType, data, socket, retries = 3, delay = 500) {
            // Sends event over the socket, with retry handling
            // Handles both synchronous (WebSocket) and async (SSE) send() methods
            const message = JSON.stringify({
                event_type: eventType,
                ...data,
                request_id: crypto.randomUUID(),
            });

            const attemptSend = async (attempt = 0) => {
                try {
                    const result = socket.send(message);

                    // If send() returns a Promise (SSE), await it and handle errors
                    if (result instanceof Promise) {
                        try {
                            await result;
                        } catch (error) {
                            // Promise rejection - handle retry logic
                            if (attempt < retries) {
                                const backoffDelay = delay * Math.pow(2, attempt);
                                await new Promise((resolve) => setTimeout(resolve, backoffDelay));
                                return attemptSend(attempt + 1);
                            } else {
                                throw new Error(`Failed to send socket message after ${retries} retries: ${error.message}`);
                            }
                        }
                    }
                    // If send() is synchronous (WebSocket), any errors are caught by outer try/catch
                } catch (error) {
                    // Synchronous error (WebSocket) - handle retry logic
                    if (attempt < retries) {
                        const backoffDelay = delay * Math.pow(2, attempt);
                        setTimeout(() => attemptSend(attempt + 1), backoffDelay);
                    } else {
                        // After all retries fail, throw the error
                        throw new Error(`Failed to send socket message after ${retries} retries: ${error.message}`);
                    }
                }
            };

            attemptSend().catch((error) => {
                // Handle any unhandled promise rejections
                console.error("Unhandled error in sendSocketEvent:", error);
            });
        };

        const emitter = mitt();

        // Set up emitters
        app.config.globalProperties.emitter = emitter;

        // Set up global constants with server side variables.
        app.config.globalProperties.$loadingImageSrc = loading_image_src;
        app.config.globalProperties.$startChatUrl = start_chat_url;
        app.config.globalProperties.$feedbackMailto = feedback_mailto;

        // Helper methods for sending events.
        // Attach any properties that will be sent in every $sendEvent call for a page
        app.config.globalProperties.$setEventContext = (context) => {
            eventContext.value = context;
        };
        app.config.globalProperties.$sendEvent = async (event_type, data) => {
            return sendEvent(event_type, { ...eventContext.value, ...data });
        };
        app.config.globalProperties.isDebug = is_debug;

        // initializeAmplitudeEngagement also enforces cookie consent ("all" required) — see BUG-625.
        if (user_details) {
            initializeAmplitudeEngagement(user_details, public_runtime_config?.amplitudeApiKey);
            // Inertia swaps pages without a document load, and fires `navigate` for
            // the first page as well as every later one — so this single listener
            // covers the whole session. Registered before the Inertia plugin
            // installs, so the initial page is never missed.
            router.on("navigate", (event) => {
                trackPageView({ client: event.detail.page.props.client, path: globalThis.location.pathname });
                // `navigate` also fires for a history restore, which replays a page's old props
                // without asking the server; `success` below carries the server's current answer.
                syncIntercomMessengerFromHistory(event.detail.page.props.intercom_settings, event.detail.page.props.user_details?.uuid ?? null);
            });
            // The global `success` event carries the page but not the visit, and visits may overlap
            // or fail, so each visit records its own successful answer through its `onSuccess`.
            router.on("start", (event) => {
                const visit = event.detail.visit;
                markIntercomVisitStarted(visit);
                const onSuccess = visit.onSuccess;
                visit.onSuccess = (page) => {
                    recordIntercomServerResponse(visit, page.props.intercom_settings);
                    return onSuccess(page);
                };
                // A validation-error page is a fresh server page too (Inertia has applied it by now),
                // and may carry a denial; network failures and history restores never come this way.
                const onError = visit.onError;
                visit.onError = (errors) => {
                    recordIntercomServerResponse(visit, usePage().props.intercom_settings);
                    return onError(errors);
                };
            });
            router.on("finish", (event) => {
                if (event.detail.visit.completed) {
                    applyIntercomServerResponse(event.detail.visit);
                }
            });
            // Partial reloads (`router.reload({ only: [...] })`) keep every prop they don't name,
            // so a consent change made elsewhere would leave the messenger running on stale
            // settings. Always ask the server for this one again.
            router.on("before", (event) => {
                const { only } = event.detail.visit;
                if (only.length > 0 && !only.includes("intercom_settings")) {
                    only.push("intercom_settings");
                }
            });
            // A consent change saved in another tab reaches this one only through localStorage.
            // Close the messenger first, so a failed refetch can't leave it running on revoked
            // consent; the refreshed prop boots it again only if the server still allows it.
            onCookieConsentChangedElsewhere(refreshIntercomMessengerForConsentChange);
            shutdownIntercomMessengerWithOtherTabs();
        }

        const page = usePage();

        // Watching the reactive prop (rather than only the `navigate` event) covers both a full
        // navigation and a partial reload after the Interface language save, without the client
        // ever guessing the code itself. `page.props` is undefined until Inertia's App component
        // sets the initial page - install() runs before that, so `immediate: true` must not
        // dereference it eagerly.
        watch(() => page.props?.locale, handleLocaleChange, { immediate: true });

        app.provide(
            "user_details",
            computed(() => page.props.user_details),
        );
        app.provide(
            "organization_details",
            computed(() => page.props.organization_details),
        );
        app.provide(
            "align_host",
            computed(() => page.props.align_host),
        );

        app.config.globalProperties.$sendSocketEvent = (event_type, data, socket) => {
            return sendSocketEvent(event_type, { ...eventContext.value, ...data }, socket);
        };

        // Make $sendEvent available to composition API components, as in: `const sendEvent = inject("sendEvent")`
        app.provide("sendEvent", app.config.globalProperties.$sendEvent);
        app.provide("globalProperties", app.config.globalProperties);
    },
};
